/**
 * Document chrome applied to generated files, configured once at provider
 * level rather than reimplemented by each calling component.
 *
 * @remarks
 * Every asset path is resolved against the web root, not `assets/`: this app
 * serves `public/` from the root (see `angular.json` → assets), so the logo
 * lives at `imgs/SADIN.png` and the font at `fonts/tajawal/Tajawal-Regular.ttf`.
 */
export interface IExportBrandingOptions {
  /**
   * Raster logo drawn in the PDF header. A vector source cannot be used —
   * jsPDF's `addImage` takes rasters and data URIs only.
   */
  logoUrl: string;
  /**
   * Footer text, shown bottom-start of every page. Already-resolved copy, not
   * a translation key: the module cannot know the caller's namespace.
   */
  footerText: string;
  /**
   * Link back to the source page, shown bottom-end of every page.
   * Defaults to the document's current URL at export time.
   */
  sourceUrl?: string;
  /**
   * Arabic-capable TTF, fetched and embedded lazily on the first Arabic export.
   *
   * @remarks
   * jsPDF's built-in fonts carry no Arabic glyphs, so without this an Arabic
   * document renders as empty boxes. Kept as a URL rather than a bundled
   * base64 blob so the ~56 KB never reaches the main bundle.
   */
  arabicFontUrl: string;
  /**
   * Bold face of {@link arabicFontUrl}, registered under the same family name.
   *
   * @remarks
   * Not optional decoration: `jspdf-autotable` draws header rows and the field
   * column of a transposed section with `fontStyle: 'bold'`, and jsPDF silently
   * falls back to Helvetica for a style it has no face for — which carries no
   * Arabic glyphs and renders those cells as mojibake.
   */
  arabicFontBoldUrl: string;
  /**
   * Font family name registered with jsPDF for {@link arabicFontUrl}.
   */
  arabicFontName: string;
}

/**
 * Default document chrome. Points at assets already shipping in this repo.
 */
export const EXPORT_BRANDING_DEFAULT_OPTIONS: IExportBrandingOptions = {
  logoUrl: 'imgs/SADIN.png',
  footerText: 'SADIN',
  arabicFontUrl: 'fonts/tajawal/Tajawal-Regular.ttf',
  arabicFontBoldUrl: 'fonts/tajawal/Tajawal-Bold.ttf',
  arabicFontName: 'Tajawal',
};
